// HIPAA-14 — Audit Controls (§ 164.312(b))
// ONE single card: Overview / Audit Events / Review & Integrity

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BASE = "/api/organization/compliance/hipaa/audit";

function authFetch(url: string, opts: RequestInit = {}) {
  const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers as Record<string, string> || {}),
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

function fmtDate(d?: string | null) {
  return d ? new Date(d).toLocaleString() : "—";
}
function fmtDateOnly(d?: string | null) {
  return d ? new Date(d).toLocaleDateString() : "—";
}

// ── Types ─────────────────────────────────────────────────────────────────────
type AuditSummary = {
  totalAllTime: number;
  total90Days: number;
  todayCount: number;
  weekCount: number;
  accessEvents: number;
  deniedEvents: number;
  suspiciousEvents: number;
  withIntegrityHash: number;
  withoutIntegrityHash: number;
  integrityCheckEnabled: boolean;
  retentionEnabled: boolean;
  retentionDays: number;
  minimumRetentionDays: number;
  retentionCompliant: boolean;
  lastReviewDate?: string | null;
  nextReviewDate?: string | null;
  configExists: boolean;
};

type AuditConfig = {
  id: string;
  isActive: boolean;
  retentionEnabled: boolean;
  retentionDays: number;
  retentionNotes?: string | null;
  alertOnFailedAccess: boolean;
  failedAccessAlertThreshold: number;
  alertOnSuspiciousActivity: boolean;
  suspiciousActivityWindowMinutes: number;
  integrityCheckEnabled: boolean;
  alertContactEmail?: string | null;
  reviewFrequency?: string | null;
  lastReviewDate?: string | null;
  nextReviewDate?: string | null;
  reviewedBy?: string | null;
  reviewNotes?: string | null;
  evidenceReference?: string | null;
  complianceNotes?: string | null;
  retentionCompliant: boolean;
  minimumRetentionDays: number;
  updatedAt: string;
};

type ConfigResponse = { exists: boolean; minimumRetentionDays: number; config?: AuditConfig };

type AuditEvent = {
  id: string;
  timestamp: string;
  action: string;
  entityName: string;
  entityId?: string | null;
  userId?: string | null;
  organizationId?: string | null;
  hasIntegrityHash: boolean;
};

type AuditEventDetail = AuditEvent & {
  integrityVerified?: boolean | null;
  changes?: string | null;
  ipAddress?: string | null;
};

type VerifyResult = {
  verified: boolean;
  hasHash: boolean;
  reason: string;
  recordId?: string;
  timestamp?: string;
  action?: string;
  entityName?: string;
};

const REVIEW_FREQS = ["Daily", "Weekly", "Monthly", "Quarterly", "SemiAnnual", "Annual"];

// ── Main Component ─────────────────────────────────────────────────────────────
export function HipaaAuditControlsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"overview" | "events" | "review">("overview");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["ac14-summary"] });
    qc.invalidateQueries({ queryKey: ["ac14-config"] });
    qc.invalidateQueries({ queryKey: ["ac14-events"] });
  };

  // ── Summary ──────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<AuditSummary>({
    queryKey: ["ac14-summary"],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/events/summary`);
      if (!r.ok) throw new Error();
      return r.json();
    },
    staleTime: 0,
  });

  // ── Config ───────────────────────────────────────────────────────────────────
  const configQ = useQuery<ConfigResponse>({
    queryKey: ["ac14-config"],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/config`);
      if (!r.ok) throw new Error();
      return r.json();
    },
    staleTime: 0,
  });

  // Upsert config form
  const defConfigForm = () => ({
    retentionEnabled: true,
    retentionDays: "2190",
    retentionNotes: "",
    alertOnFailedAccess: true,
    failedAccessAlertThreshold: "5",
    alertOnSuspiciousActivity: true,
    suspiciousActivityWindowMinutes: "60",
    integrityCheckEnabled: true,
    alertContactEmail: "",
    reviewFrequency: "Monthly",
    nextReviewDate: "",
    evidenceReference: "",
    complianceNotes: "",
  });
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [cfgForm, setCfgForm] = useState(defConfigForm());

  const upsertConfigMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/config`, {
        method: "POST",
        body: JSON.stringify({
          ...cfgForm,
          retentionDays: parseInt(cfgForm.retentionDays),
          failedAccessAlertThreshold: parseInt(cfgForm.failedAccessAlertThreshold),
          suspiciousActivityWindowMinutes: parseInt(cfgForm.suspiciousActivityWindowMinutes),
          nextReviewDate: cfgForm.nextReviewDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Audit control configuration saved." });
      setShowConfigForm(false);
      setCfgForm(defConfigForm());
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Record review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewedBy: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" });
  const recordReviewMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/config/review`, {
        method: "PATCH",
        body: JSON.stringify({ ...reviewForm, nextReviewDate: reviewForm.nextReviewDate || null }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Failed"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Audit review recorded." });
      setShowReviewForm(false);
      setReviewForm({ reviewedBy: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // ── Audit Events ─────────────────────────────────────────────────────────────
  const [eventsPage, setEventsPage] = useState(1);
  const [filterAction, setFilterAction] = useState("");
  const [filterEntity, setFilterEntity] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterHasHash, setFilterHasHash] = useState<string>("");

  const eventsQ = useQuery<{ total: number; page: number; pageSize: number; items: AuditEvent[] }>({
    queryKey: ["ac14-events", eventsPage, filterAction, filterEntity, filterFrom, filterTo, filterSearch, filterHasHash],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(eventsPage), pageSize: "20" });
      if (filterAction)  p.set("action", filterAction);
      if (filterEntity)  p.set("entityName", filterEntity);
      if (filterFrom)    p.set("from", filterFrom);
      if (filterTo)      p.set("to", filterTo);
      if (filterSearch)  p.set("search", filterSearch);
      if (filterHasHash === "true")  p.set("hasHash", "true");
      if (filterHasHash === "false") p.set("hasHash", "false");
      const r = await authFetch(`${BASE}/events?${p}`);
      if (!r.ok) throw new Error();
      return r.json();
    },
    staleTime: 0,
  });

  // Event detail modal
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<AuditEventDetail>({
    queryKey: ["ac14-event-detail", detailId],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/events/${detailId}`);
      if (!r.ok) throw new Error();
      return r.json();
    },
    enabled: detailId !== null,
    staleTime: 0,
  });

  // Integrity verify
  const verifyMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/events/${id}/verify`, { method: "POST", body: JSON.stringify({}) });
      if (!r.ok) throw new Error("Verify request failed");
      return r.json() as Promise<VerifyResult>;
    },
    onSuccess: (result: VerifyResult) => {
      toast({
        title: result.verified ? "Integrity verified ✓" : "Integrity check FAILED",
        description: result.reason,
        variant: result.verified ? "default" : "destructive",
      });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const cfg = configQ.data?.config;
  const eventsTotal = eventsQ.data?.total ?? 0;
  const eventsTotalPages = Math.ceil(eventsTotal / 20);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Controls (§ 164.312(b))</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
      {/* Summary tiles */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: "Total Events (All Time)", value: s.totalAllTime },
            { label: "Events Today",            value: s.todayCount,          warn: false },
            { label: "Events (7 days)",         value: s.weekCount },
            { label: "Access Events (90d)",     value: s.accessEvents },
            { label: "Denied / Failed (90d)",   value: s.deniedEvents,        warn: s.deniedEvents > 0 },
            { label: "Suspicious (90d)",        value: s.suspiciousEvents,    warn: s.suspiciousEvents > 0 },
            { label: "With Integrity Hash",     value: s.withIntegrityHash },
            { label: "Missing Hash (pre-HIPAA)",value: s.withoutIntegrityHash, warn: s.withoutIntegrityHash > 0 },
          ].map(c => (
            <div key={c.label} className={`border rounded p-2 text-center ${c.warn ? "border-destructive/40 bg-destructive/5" : ""}`}>
              <div className={`text-xl font-bold ${c.warn ? "text-destructive" : ""}`}>{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alerts */}
      {s && !s.configExists && (
        <div className="border border-amber-400/40 rounded p-3 bg-amber-50/30 text-sm text-amber-800">
          No audit control configuration found. HIPAA requires a documented retention policy of at least 6 years (2,190 days).
          {canManage && <Button size="sm" className="ml-3 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => { setTab("review"); setShowConfigForm(true); }}>Configure Now</Button>}
        </div>
      )}
      {s && s.configExists && !s.retentionCompliant && (
        <div className="border border-destructive/40 rounded p-3 bg-destructive/5 text-sm">
          <span className="font-medium text-destructive">Retention non-compliant:</span>{" "}
          Current {s.retentionDays} days is below the HIPAA minimum of {s.minimumRetentionDays} days (6 years).
        </div>
      )}
      {s && s.nextReviewDate && new Date(s.nextReviewDate) < new Date() && (
        <div className="border border-amber-400/40 rounded p-3 bg-amber-50/30 text-sm text-amber-800">
          Audit review overdue — scheduled review date passed on {fmtDateOnly(s.nextReviewDate)}.
        </div>
      )}

      {/* Internal tabs */}
      <div className="flex gap-2 border-b pb-2">
        {(["overview", "events", "review"] as const).map(t => (
          <button
            key={t}
            className={`text-sm font-medium px-3 py-1 rounded-t capitalize ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            onClick={() => setTab(t)}
          >
            {t === "overview" ? "Overview" : t === "events" ? "Audit Events" : "Review & Config"}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && s && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Activity */}
            <div className="border rounded p-3 space-y-2">
              <p className="font-medium text-sm">Activity Monitoring</p>
              <p className="text-xs text-muted-foreground">§ 164.312(b) — Record and examine ePHI system activity</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Total (all time)</span><span className="font-bold">{s.totalAllTime.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Today</span><span className="font-bold">{s.todayCount}</span></div>
                <div className="flex justify-between"><span>Last 7 days</span><span className="font-bold">{s.weekCount}</span></div>
                <div className="flex justify-between"><span>Last 90 days</span><span className="font-bold">{s.total90Days}</span></div>
              </div>
            </div>
            {/* Integrity */}
            <div className="border rounded p-3 space-y-2">
              <p className="font-medium text-sm">Tamper Protection</p>
              <p className="text-xs text-muted-foreground">HMAC-SHA256 integrity verification</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Protected (hashed)</span><Badge variant="default">{s.withIntegrityHash}</Badge></div>
                <div className="flex justify-between"><span>Pre-HIPAA (no hash)</span><Badge variant={s.withoutIntegrityHash > 0 ? "outline" : "secondary"}>{s.withoutIntegrityHash}</Badge></div>
                <div className="flex justify-between"><span>Integrity checks</span><Badge variant={s.integrityCheckEnabled ? "default" : "destructive"}>{s.integrityCheckEnabled ? "Enabled" : "Disabled"}</Badge></div>
              </div>
            </div>
            {/* Retention */}
            <div className="border rounded p-3 space-y-2">
              <p className="font-medium text-sm">Retention (§ 164.312(b))</p>
              <p className="text-xs text-muted-foreground">Minimum 6 years (2,190 days) per HIPAA</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Configured</span><Badge variant={s.configExists ? "default" : "destructive"}>{s.configExists ? "Yes" : "No"}</Badge></div>
                <div className="flex justify-between"><span>Retention period</span><span className="font-bold">{s.configExists ? `${s.retentionDays} days` : "—"}</span></div>
                <div className="flex justify-between"><span>Compliant</span><Badge variant={s.retentionCompliant ? "default" : "destructive"}>{s.retentionCompliant ? "Yes" : "No"}</Badge></div>
                {s.lastReviewDate && <div className="text-xs text-muted-foreground">Last review: {fmtDateOnly(s.lastReviewDate)}</div>}
              </div>
            </div>
          </div>
          {/* Suspicious activity panel */}
          {s.suspiciousEvents > 0 && (
            <div className="border border-destructive/30 rounded p-3 bg-destructive/5">
              <p className="font-medium text-sm text-destructive mb-1">Suspicious Activity Detected (last 90 days)</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div className="flex justify-between"><span>Denied/Failed</span><span className="font-bold text-destructive">{s.deniedEvents}</span></div>
                <div className="flex justify-between"><span>Suspicious total</span><span className="font-bold text-destructive">{s.suspiciousEvents}</span></div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Review the Audit Events tab to investigate specific events.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Audit Events ── */}
      {tab === "events" && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Input placeholder="Search action or entity…" value={filterSearch} onChange={e => { setFilterSearch(e.target.value); setEventsPage(1); }} />
            <Input placeholder="Action (e.g. PolicyCreated)" value={filterAction} onChange={e => { setFilterAction(e.target.value); setEventsPage(1); }} />
            <Input placeholder="Entity name" value={filterEntity} onChange={e => { setFilterEntity(e.target.value); setEventsPage(1); }} />
            <select className="border rounded px-2 py-1.5 text-sm" value={filterHasHash} onChange={e => { setFilterHasHash(e.target.value); setEventsPage(1); }}>
              <option value="">All Records</option>
              <option value="true">Has Integrity Hash</option>
              <option value="false">No Integrity Hash</option>
            </select>
            <div className="flex gap-2 items-center col-span-2 md:col-span-2">
              <Input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setEventsPage(1); }} title="From date" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" value={filterTo}   onChange={e => { setFilterTo(e.target.value);   setEventsPage(1); }} title="To date" />
            </div>
            <div className="col-span-2 md:col-span-2 flex justify-end">
              <Button size="sm" variant="outline" onClick={() => {
                setFilterSearch(""); setFilterAction(""); setFilterEntity("");
                setFilterFrom(""); setFilterTo(""); setFilterHasHash(""); setEventsPage(1);
              }}>Clear Filters</Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Showing {eventsQ.data?.items.length ?? 0} of {eventsTotal} audit events. Audit records are read-only and protected from modification.
          </p>

          {eventsQ.isLoading ? <Skeleton className="h-40 w-full" /> : (
            <div className="space-y-1">
              {(eventsQ.data?.items ?? []).map(ev => (
                <div key={ev.id} className="border rounded p-2.5 flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{ev.action}</span>
                      <Badge variant="outline" className="text-xs">{ev.entityName}</Badge>
                      {ev.hasIntegrityHash
                        ? <Badge variant="default" className="text-xs">✓ Protected</Badge>
                        : <Badge variant="outline" className="text-xs text-muted-foreground">No Hash</Badge>
                      }
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {fmtDate(ev.timestamp)}{ev.userId ? ` · Actor: ${String(ev.userId).substring(0, 8)}…` : ""}{ev.entityId ? ` · ID: ${String(ev.entityId).substring(0, 12)}…` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setDetailId(ev.id)}>Detail</Button>
                    {ev.hasIntegrityHash && (
                      <Button size="sm" variant="ghost" onClick={() => verifyMut.mutate(ev.id)} disabled={verifyMut.isPending}>Verify</Button>
                    )}
                  </div>
                </div>
              ))}
              {eventsTotal === 0 && !eventsQ.isLoading && (
                <p className="text-sm text-muted-foreground text-center py-6">No audit events match the current filters.</p>
              )}
            </div>
          )}

          {eventsTotalPages > 1 && (
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" disabled={eventsPage <= 1} onClick={() => setEventsPage(p => p - 1)}>Prev</Button>
              <span className="text-sm py-1">{eventsPage} / {eventsTotalPages}</span>
              <Button size="sm" variant="outline" disabled={eventsPage >= eventsTotalPages} onClick={() => setEventsPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </div>
      )}

      {/* ── Review & Config ── */}
      {tab === "review" && (
        <div className="space-y-4">
          {/* Current config */}
          {cfg ? (
            <div className="border rounded p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Current Audit Control Configuration</p>
                  <p className="text-xs text-muted-foreground">Last updated: {fmtDate(cfg.updatedAt)}</p>
                </div>
                {canManage && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => {
                      setCfgForm({
                        retentionEnabled: cfg.retentionEnabled,
                        retentionDays: String(cfg.retentionDays),
                        retentionNotes: cfg.retentionNotes || "",
                        alertOnFailedAccess: cfg.alertOnFailedAccess,
                        failedAccessAlertThreshold: String(cfg.failedAccessAlertThreshold),
                        alertOnSuspiciousActivity: cfg.alertOnSuspiciousActivity,
                        suspiciousActivityWindowMinutes: String(cfg.suspiciousActivityWindowMinutes),
                        integrityCheckEnabled: cfg.integrityCheckEnabled,
                        alertContactEmail: cfg.alertContactEmail || "",
                        reviewFrequency: cfg.reviewFrequency || "Monthly",
                        nextReviewDate: cfg.nextReviewDate ? cfg.nextReviewDate.substring(0, 10) : "",
                        evidenceReference: cfg.evidenceReference || "",
                        complianceNotes: cfg.complianceNotes || "",
                      });
                      setShowConfigForm(true);
                    }}>Edit Config</Button>
                    <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowReviewForm(true)}>Record Review</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="border rounded p-2">
                  <p className="text-xs text-muted-foreground">Retention Period</p>
                  <p className="font-bold">{cfg.retentionDays} days</p>
                  <Badge variant={cfg.retentionCompliant ? "default" : "destructive"} className="text-xs mt-1">
                    {cfg.retentionCompliant ? "Compliant" : `Below ${cfg.minimumRetentionDays}d minimum`}
                  </Badge>
                </div>
                <div className="border rounded p-2">
                  <p className="text-xs text-muted-foreground">Integrity Checks</p>
                  <Badge variant={cfg.integrityCheckEnabled ? "default" : "destructive"}>{cfg.integrityCheckEnabled ? "Enabled" : "Disabled"}</Badge>
                </div>
                <div className="border rounded p-2">
                  <p className="text-xs text-muted-foreground">Failed Access Alert</p>
                  <p className="font-bold">{cfg.alertOnFailedAccess ? `After ${cfg.failedAccessAlertThreshold} attempts` : "Disabled"}</p>
                </div>
                <div className="border rounded p-2">
                  <p className="text-xs text-muted-foreground">Review Frequency</p>
                  <p className="font-bold">{cfg.reviewFrequency || "—"}</p>
                  {cfg.nextReviewDate && <p className="text-xs text-muted-foreground">Next: {fmtDateOnly(cfg.nextReviewDate)}</p>}
                </div>
              </div>
              {cfg.lastReviewDate && (
                <div className="text-sm text-muted-foreground">
                  Last reviewed by <span className="font-medium">{cfg.reviewedBy || "unknown"}</span> on {fmtDateOnly(cfg.lastReviewDate)}
                  {cfg.reviewNotes && <p className="italic mt-0.5">"{cfg.reviewNotes}"</p>}
                </div>
              )}
              {cfg.evidenceReference && <p className="text-xs text-muted-foreground">Evidence: {cfg.evidenceReference}</p>}
            </div>
          ) : (
            <div className="border rounded p-4 space-y-2">
              <p className="text-sm font-medium">No audit control configuration</p>
              <p className="text-xs text-muted-foreground">HIPAA § 164.312(b) requires implementation of hardware, software, and procedural mechanisms to record and examine ePHI access activity. Configure your audit retention policy to begin compliance tracking.</p>
              {canManage && <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowConfigForm(true)}>Configure Audit Controls</Button>}
            </div>
          )}

          {/* Upsert config form */}
          {showConfigForm && (
            <div className="border rounded p-4 space-y-4 bg-muted/30">
              <h4 className="font-medium">{cfg ? "Update Audit Control Configuration" : "Configure Audit Controls"}</h4>
              <p className="text-xs text-muted-foreground">Creating a new configuration deactivates the previous one and is recorded in the audit log.</p>

              <div className="space-y-3">
                <p className="text-sm font-medium">Retention</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Retention Period (days) — minimum 2,190 (6 years)</label>
                    <Input type="number" value={cfgForm.retentionDays} onChange={e => setCfgForm(f => ({ ...f, retentionDays: e.target.value }))} />
                    {parseInt(cfgForm.retentionDays) < 2190 && (
                      <p className="text-xs text-destructive mt-0.5">Below HIPAA minimum of 2,190 days</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium">Retention Notes</label>
                    <Input value={cfgForm.retentionNotes} onChange={e => setCfgForm(f => ({ ...f, retentionNotes: e.target.value }))} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={cfgForm.retentionEnabled} onChange={e => setCfgForm(f => ({ ...f, retentionEnabled: e.target.checked }))} />
                  Retention policy enabled
                </label>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Alert Configuration</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium">Failed Access Threshold</label>
                    <Input type="number" value={cfgForm.failedAccessAlertThreshold} onChange={e => setCfgForm(f => ({ ...f, failedAccessAlertThreshold: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Suspicious Activity Window (min)</label>
                    <Input type="number" value={cfgForm.suspiciousActivityWindowMinutes} onChange={e => setCfgForm(f => ({ ...f, suspiciousActivityWindowMinutes: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Alert Contact Email</label>
                    <Input type="email" value={cfgForm.alertContactEmail} onChange={e => setCfgForm(f => ({ ...f, alertContactEmail: e.target.value }))} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  {([
                    ["alertOnFailedAccess", "Alert on Failed Access"],
                    ["alertOnSuspiciousActivity", "Alert on Suspicious Activity"],
                    ["integrityCheckEnabled", "Integrity Checks Enabled"],
                  ] as [keyof typeof cfgForm, string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cfgForm[k] as boolean} onChange={e => setCfgForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Review Schedule</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium">Review Frequency</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={cfgForm.reviewFrequency} onChange={e => setCfgForm(f => ({ ...f, reviewFrequency: e.target.value }))}>
                      {REVIEW_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Next Review Date</label>
                    <Input type="date" value={cfgForm.nextReviewDate} onChange={e => setCfgForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Evidence Reference</label>
                    <Input value={cfgForm.evidenceReference} onChange={e => setCfgForm(f => ({ ...f, evidenceReference: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => upsertConfigMut.mutate()} disabled={upsertConfigMut.isPending}>
                  {upsertConfigMut.isPending ? "Saving…" : "Save Configuration"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowConfigForm(false); setCfgForm(defConfigForm()); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Record review form */}
          {showReviewForm && (
            <div className="border rounded p-4 space-y-3 bg-muted/30">
              <h4 className="font-medium">Record Audit Review</h4>
              <p className="text-xs text-muted-foreground">Document your periodic review of audit logs and controls per § 164.312(b).</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs font-medium">Reviewed By *</label><Input value={reviewForm.reviewedBy} onChange={e => setReviewForm(f => ({ ...f, reviewedBy: e.target.value }))} placeholder="Compliance officer name or ID" /></div>
                <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} /></div>
                <div><label className="text-xs font-medium">Evidence Reference</label><Input value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="AUDIT-REVIEW-2026-Q3" /></div>
                <div><label className="text-xs font-medium">Review Notes</label><Input value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => recordReviewMut.mutate()} disabled={recordReviewMut.isPending}>
                  {recordReviewMut.isPending ? "Saving…" : "Record Review"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowReviewForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Integrity panel */}
          <div className="border rounded p-3 space-y-2">
            <p className="font-medium text-sm">Integrity Protection</p>
            <p className="text-xs text-muted-foreground">
              Audit records are protected with HMAC-SHA256 tamper detection. Records created after the integrity migration carry a stored hash that can be verified at any time. Records created before the migration have no hash and are noted as pre-HIPAA.
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="border rounded p-2 text-center">
                <div className="text-lg font-bold text-green-700">{s?.withIntegrityHash ?? "—"}</div>
                <div className="text-xs text-muted-foreground">Protected Records (90d)</div>
              </div>
              <div className="border rounded p-2 text-center">
                <div className="text-lg font-bold">{s?.withoutIntegrityHash ?? "—"}</div>
                <div className="text-xs text-muted-foreground">Pre-HIPAA (no hash, 90d)</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              To verify the integrity of a specific record, navigate to the Audit Events tab and click "Verify" on any protected record.
            </p>
          </div>
        </div>
      )}

      {/* ── Event Detail Modal ── */}
      {detailId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <h4 className="font-medium">Audit Event Detail</h4>
            {detailQ.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : detailQ.data ? (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-xs text-muted-foreground">Action</span><p className="font-medium">{detailQ.data.action}</p></div>
                  <div><span className="text-xs text-muted-foreground">Entity</span><p>{detailQ.data.entityName}</p></div>
                  <div><span className="text-xs text-muted-foreground">Timestamp</span><p>{fmtDate(detailQ.data.timestamp)}</p></div>
                  <div><span className="text-xs text-muted-foreground">Actor (User ID)</span><p className="font-mono text-xs">{detailQ.data.userId ?? "—"}</p></div>
                  <div><span className="text-xs text-muted-foreground">Entity ID</span><p className="font-mono text-xs">{detailQ.data.entityId ?? "—"}</p></div>
                  <div>
                    <span className="text-xs text-muted-foreground">Integrity</span>
                    <div>
                      {detailQ.data.integrityVerified === true  && <Badge variant="default">Verified ✓</Badge>}
                      {detailQ.data.integrityVerified === false && <Badge variant="destructive">MISMATCH ✗</Badge>}
                      {detailQ.data.integrityVerified === null  && (
                        <Badge variant="outline">{detailQ.data.hasIntegrityHash ? "Not checked" : "No hash (pre-HIPAA)"}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                {detailQ.data.changes && (
                  <div>
                    <span className="text-xs text-muted-foreground">Changes</span>
                    <p className="font-mono text-xs bg-muted rounded p-2 mt-0.5 break-all">{detailQ.data.changes}</p>
                  </div>
                )}
                {detailQ.data.ipAddress && (
                  <div>
                    <span className="text-xs text-muted-foreground">IP Address</span>
                    <p className="font-mono text-xs">{detailQ.data.ipAddress}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground border-t pt-2">
                  Audit records are immutable and protected from modification through this interface.
                </p>
              </div>
            ) : null}
            <div className="flex gap-2 justify-between">
              {detailQ.data?.hasIntegrityHash && (
                <Button size="sm" variant="outline" onClick={() => { verifyMut.mutate(detailId); setDetailId(null); }}>
                  Verify Integrity
                </Button>
              )}
              <Button size="sm" className="ml-auto" onClick={() => setDetailId(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}
